// The SPA document link comes back from Cloone's `POST /sales/create`, which book-web calls from
// the browser on every load of the Lawyer tab (Cases.jsx fetchSPALink). Cloone answers that call
// with a 500 and an EMPTY body when it does not like the case -- the 2026-09-17 QA pass on
// book-dev hit it six times across three page loads -- and `res.json()` on an empty body throws
// OUTSIDE the try/catch that guards the fetch, so every one of those became an uncaught promise
// rejection in the page and no Bugsnag report. Reading the response is now one guarded step.

export class SpaDocLinkError extends Error {
  constructor(status, body) {
    super(`Cloone sales/create answered ${status}${body ? `: ${body.slice(0, 120)}` : ''}`);
    this.name = 'SpaDocLinkError';
    this.status = status;
  }
}

/**
 * @param {Response|undefined} res  the fetch Response, or undefined when the fetch itself threw
 * @returns {Promise<string|null>}  the SPA doc link, or null when there is none to show
 * @throws {SpaDocLinkError}        on a non-2xx answer or an unparseable body -- so the caller
 *                                  can notify Bugsnag once instead of leaking a rejection
 */
export async function readSpaDocLink(res) {
  if (!res) return null;
  let text = '';
  try {
    text = await res.text();
  } catch (e) {
    // A cancellation (supersede, unmount, timeout) keeps its identity: the caller decides what an
    // AbortError means, and wrapping it here would turn a silent abandon into a Bugsnag report.
    if (e && e.name === 'AbortError') throw e;
    // Any other body-stream failure on a 2xx is not "no link" -- it is the read failure this
    // module exists to report. Keep the status and the read error's message on the thrown error.
    throw new SpaDocLinkError(res.status, `body read failed: ${(e && e.message) || e}`);
  }
  if (!res.ok) throw new SpaDocLinkError(res.status, text);
  if (!text) return null;
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new SpaDocLinkError(res.status, text);
  }
  return (data && typeof data.spa_doc_link === 'string' && data.spa_doc_link) || null;
}

/**
 * The link to SHOW for the active SPA case: only a link fetched for that very case. State can
 * hold case A's link for a moment after the case list has already switched to B (the list is
 * published before the link request for B goes out), so the render gates on ownership rather
 * than trusting whatever link is in state.
 */
export const spaLinkForCase = (spaCase, link, ownerCaseID) => (
  spaCase && link && ownerCaseID === spaCase.id ? link : null
);



// WEBPACK FOOTER //
// ./src/containers/Lawyer/spaDocLink.js